import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Mail, MessageSquare, ChevronDown, ChevronUp, Save, RotateCcw, Eye, Copy } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EVENT_ORDER = [
  'Application Received',
  'Advanced to Phone Interview',
  'Interview Scheduled',
  'Phone Interview Passed',
  'Advanced to In-Person Interview',
  'In-Person Interview Completed',
  'Hired',
  'Not Moving Forward',
  'Interview Reminder (24h)',
];

const MessageTemplatesTab = () => {
  const [templates, setTemplates] = useState([]);
  const [placeholderDescs, setPlaceholderDescs] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [edits, setEdits] = useState({});
  const [saving, setSaving] = useState({});
  const [previewing, setPreviewing] = useState({});
  const [previews, setPreviews] = useState({});

  useEffect(() => { fetchTemplates(); }, []);

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API}/message-templates`);
      setTemplates(res.data.templates);
      setPlaceholderDescs(res.data.placeholder_descriptions);
    } catch {
      toast.error('Failed to load message templates');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (key) => setExpanded(prev => ({ ...prev, [key]: !prev[key] }));

  const getEdit = (key, field) => {
    if (edits[key] && edits[key][field] !== undefined) return edits[key][field];
    const tmpl = templates.find(t => t.event_key === key);
    return tmpl ? tmpl[field] || '' : '';
  };

  const setEdit = (key, field, value) => {
    setEdits(prev => ({ ...prev, [key]: { ...prev[key], [field]: value } }));
  };

  const isDirty = (key) => {
    const e = edits[key];
    if (!e) return false;
    const tmpl = templates.find(t => t.event_key === key);
    if (!tmpl) return false;
    return (e.subject !== undefined && e.subject !== (tmpl.subject || '')) ||
           (e.body !== undefined && e.body !== tmpl.body);
  };

  const saveTemplate = async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const payload = { body: getEdit(key, 'body') };
      const tmpl = templates.find(t => t.event_key === key);
      if (tmpl?.channel === 'email') payload.subject = getEdit(key, 'subject');
      const res = await axios.put(`${API}/message-templates/${key}`, payload);
      setTemplates(prev => prev.map(t => t.event_key === key ? res.data : t));
      setEdits(prev => { const n = { ...prev }; delete n[key]; return n; });
      toast.success('Template saved');
    } catch {
      toast.error('Failed to save template');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const resetTemplate = async (key) => {
    if (!window.confirm('Reset this template to default?')) return;
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const res = await axios.post(`${API}/message-templates/${key}/reset`);
      setTemplates(prev => prev.map(t => t.event_key === key ? res.data : t));
      setEdits(prev => { const n = { ...prev }; delete n[key]; return n; });
      setPreviews(prev => { const n = { ...prev }; delete n[key]; return n; });
      toast.success('Template reset to default');
    } catch {
      toast.error('Failed to reset template');
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  };

  const previewTemplate = async (key) => {
    setPreviewing(prev => ({ ...prev, [key]: true }));
    try {
      const payload = { body: getEdit(key, 'body') };
      const tmpl = templates.find(t => t.event_key === key);
      if (tmpl?.channel === 'email') payload.subject = getEdit(key, 'subject');
      const res = await axios.post(`${API}/message-templates/${key}/preview`, payload);
      setPreviews(prev => ({ ...prev, [key]: res.data }));
    } catch {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewing(prev => ({ ...prev, [key]: false }));
    }
  };

  const insertPlaceholder = (key, field, placeholder) => {
    const tag = `{${placeholder}}`;
    const current = getEdit(key, field);
    setEdit(key, field, current + tag);
    toast.info(`Inserted ${tag}`);
  };

  // Group templates by event
  const grouped = {};
  templates.forEach(t => {
    if (!grouped[t.event]) grouped[t.event] = [];
    grouped[t.event].push(t);
  });

  const sortedEvents = EVENT_ORDER.filter(e => grouped[e]);

  if (loading) {
    return (
      <Card><CardContent className="py-12 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-3 border-[#4B2D6E] border-t-transparent" />
      </CardContent></Card>
    );
  }

  return (
    <Card data-testid="message-templates-tab">
      <CardHeader>
        <CardTitle>Message Templates</CardTitle>
        <CardDescription>Customize automated notifications sent to candidates and admins at each milestone</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {sortedEvents.map(eventName => (
          <div key={eventName} className="border rounded-lg overflow-hidden">
            {/* Event Header */}
            <button
              onClick={() => toggleExpand(eventName)}
              className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
              data-testid={`event-toggle-${eventName.replace(/[^a-zA-Z]/g, '-').toLowerCase()}`}
            >
              <div className="flex items-center gap-3">
                <h3 className="font-semibold text-[#4B2D6E] text-sm">{eventName}</h3>
                <div className="flex gap-1.5">
                  {grouped[eventName].map(t => (
                    <Badge
                      key={t.event_key}
                      variant="outline"
                      className={`text-[10px] py-0 ${t.channel === 'email' ? 'border-blue-300 text-blue-600' : 'border-green-300 text-green-600'}`}
                    >
                      {t.channel === 'email' ? 'Email' : 'SMS'} → {t.recipient}
                    </Badge>
                  ))}
                </div>
              </div>
              {expanded[eventName] ? <ChevronUp className="h-4 w-4 text-[#999]" /> : <ChevronDown className="h-4 w-4 text-[#999]" />}
            </button>

            {/* Expanded Templates */}
            {expanded[eventName] && (
              <div className="px-4 py-4 space-y-5">
                {grouped[eventName].map(tmpl => (
                  <div key={tmpl.event_key} className="border rounded-lg p-4 space-y-3" data-testid={`template-${tmpl.event_key}`}>
                    {/* Template Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {tmpl.channel === 'email'
                          ? <Mail className="h-4 w-4 text-blue-500" />
                          : <MessageSquare className="h-4 w-4 text-green-500" />
                        }
                        <span className="font-medium text-sm text-[#333]">
                          {tmpl.channel === 'email' ? 'Email' : 'SMS'} to {tmpl.recipient}
                        </span>
                        {!tmpl.is_default && (
                          <Badge className="bg-amber-100 text-amber-700 text-[10px]">Customized</Badge>
                        )}
                      </div>
                      <div className="flex gap-1.5">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => previewTemplate(tmpl.event_key)}
                          disabled={previewing[tmpl.event_key]}
                          data-testid={`preview-${tmpl.event_key}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          {previewing[tmpl.event_key] ? '...' : 'Preview'}
                        </Button>
                        {!tmpl.is_default && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resetTemplate(tmpl.event_key)}
                            className="text-[#999]"
                            data-testid={`reset-${tmpl.event_key}`}
                          >
                            <RotateCcw className="h-3.5 w-3.5 mr-1" /> Reset
                          </Button>
                        )}
                      </div>
                    </div>

                    {/* Subject (email only) */}
                    {tmpl.channel === 'email' && (
                      <div>
                        <Label className="text-xs text-[#666]">Subject Line</Label>
                        <Input
                          value={getEdit(tmpl.event_key, 'subject')}
                          onChange={e => setEdit(tmpl.event_key, 'subject', e.target.value)}
                          className="mt-1 text-sm"
                          data-testid={`subject-${tmpl.event_key}`}
                        />
                      </div>
                    )}

                    {/* Body */}
                    <div>
                      <Label className="text-xs text-[#666]">
                        {tmpl.channel === 'email' ? 'Email Body (HTML supported)' : 'SMS Message'}
                      </Label>
                      <Textarea
                        value={getEdit(tmpl.event_key, 'body')}
                        onChange={e => setEdit(tmpl.event_key, 'body', e.target.value)}
                        rows={tmpl.channel === 'email' ? 6 : 3}
                        className="mt-1 text-sm font-mono"
                        data-testid={`body-${tmpl.event_key}`}
                      />
                    </div>

                    {/* Placeholders */}
                    <div>
                      <Label className="text-xs text-[#666] mb-1.5 block">Available Placeholders — click to insert</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {(tmpl.placeholders || []).map(ph => (
                          <button
                            key={ph}
                            onClick={() => insertPlaceholder(tmpl.event_key, tmpl.channel === 'email' ? 'body' : 'body', ph)}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#4B2D6E]/5 hover:bg-[#4B2D6E]/10 text-[#4B2D6E] text-xs transition-colors"
                            title={placeholderDescs[ph] || ph}
                            data-testid={`ph-${tmpl.event_key}-${ph}`}
                          >
                            <Copy className="h-2.5 w-2.5" />
                            {`{${ph}}`}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Preview Area */}
                    {previews[tmpl.event_key] && (
                      <div className="border rounded-lg bg-blue-50/50 p-4" data-testid={`preview-area-${tmpl.event_key}`}>
                        <p className="text-xs font-semibold text-blue-600 mb-2 flex items-center gap-1">
                          <Eye className="h-3 w-3" /> Preview (with sample data)
                        </p>
                        {tmpl.channel === 'email' && previews[tmpl.event_key].subject && (
                          <p className="text-sm font-semibold text-[#333] mb-2">
                            Subject: {previews[tmpl.event_key].subject}
                          </p>
                        )}
                        <div
                          className="text-sm text-[#333] bg-white rounded p-3 border"
                          dangerouslySetInnerHTML={{
                            __html: tmpl.channel === 'email'
                              ? previews[tmpl.event_key].body
                              : previews[tmpl.event_key].body.replace(/\n/g, '<br>')
                          }}
                        />
                      </div>
                    )}

                    {/* Save Button */}
                    {isDirty(tmpl.event_key) && (
                      <Button
                        onClick={() => saveTemplate(tmpl.event_key)}
                        disabled={saving[tmpl.event_key]}
                        className="bg-[#E8722A] hover:bg-[#D6621F]"
                        data-testid={`save-${tmpl.event_key}`}
                      >
                        <Save className="h-3.5 w-3.5 mr-1.5" />
                        {saving[tmpl.event_key] ? 'Saving...' : 'Save Template'}
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export { MessageTemplatesTab };
